import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  FileText,
  MoreVertical,
  Edit2,
  Trash2,
  Eye,
  User,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  AdminClientApi,
  ClientThoughtWithAuthor,
  GetThoughtsInputInterface,
} from '../../Admin-ClientLibrary';
import './Thoughts.css';
import { getApiUrl } from '../../config/api';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const Thoughts: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [thoughts, setThoughts] = useState<ClientThoughtWithAuthor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>(searchParams.get('search') || '');
  const [selectedThoughts, setSelectedThoughts] = useState<Set<number>>(new Set());
  const [totalThoughts, setTotalThoughts] = useState<number>(0);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Page history stack for cursor-based pagination
  const [pageHistory, setPageHistory] = useState<
    Array<{
      cursor: {
        beforeId?: number;
        afterId?: number;
        beforeCreatedDate?: string;
        afterCreatedDate?: string;
      } | null;
      pageNum: number;
    }>
  >([{ cursor: null, pageNum: 1 }]);

  // Pagination and ordering state
  const [orderBy, setOrderBy] = useState<string>(searchParams.get('orderBy') || 'id');
  const [orderDirection, setOrderDirection] = useState<string>(
    searchParams.get('orderDirection') || 'DESC'
  );

  // Initialize filters from URL parameters
  const [filters, setFilters] = useState({
    createdAfter: searchParams.get('createdAfter') || '',
    createdBefore: searchParams.get('createdBefore') || '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    createdAfter: searchParams.get('createdAfter') || '',
    createdBefore: searchParams.get('createdBefore') || '',
  });

  const limit = 20;

  // Function to update URL parameters
  const updateUrlParams = (params: Record<string, string | number | undefined>): void => {
    const newParams = new URLSearchParams();

    // Add all current search params first
    searchParams.forEach((value, key) => {
      newParams.set(key, value);
    });

    // Update with new params
    Object.entries(params).forEach(([key, value]) => {
      if (value && value !== '') {
        newParams.set(key, value.toString());
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  };

  // Fetch thoughts with pagination and filtering
  const fetchThoughts = async (
    direction: 'first' | 'next' | 'prev',
    cursor?: {
      beforeId?: number;
      afterId?: number;
      beforeCreatedDate?: string;
      afterCreatedDate?: string;
    },
    search?: string,
    filterParams?: { createdAfter: string; createdBefore: string },
    orderByParam?: string,
    orderDirectionParam?: string,
    getCount: boolean = false
  ): Promise<void> => {
    setLoading(true);
    setError('');

    try {
      const apiParams: GetThoughtsInputInterface = {
        limit: 20,
        getCount,
      };

      // Add search if provided
      if (search) {
        apiParams.search = search;
      }

      // Add order parameters
      if (orderByParam) {
        apiParams.orderBy = orderByParam;
      }
      if (orderDirectionParam) {
        apiParams.orderDirection = orderDirectionParam;
      }

      // Add cursor parameters if provided
      if (cursor) {
        if (cursor.beforeId) apiParams.beforeId = cursor.beforeId;
        if (cursor.afterId) apiParams.afterId = cursor.afterId;
        if (cursor.beforeCreatedDate) apiParams.beforeCreatedDate = cursor.beforeCreatedDate;
        if (cursor.afterCreatedDate) apiParams.afterCreatedDate = cursor.afterCreatedDate;
      }

      // Add filter parameters if provided
      if (filterParams) {
        // Only add filter parameters if they have values
        if (filterParams.createdAfter) {
          apiParams.createdAfter = filterParams.createdAfter;
        }
        if (filterParams.createdBefore) {
          apiParams.createdBefore = filterParams.createdBefore;
        }
      }

      console.log('Fetching thoughts with params:', apiParams);

      const response = await adminApi.getThoughts(apiParams);

      if (response.success) {
        setThoughts(response.thoughts);
        if (getCount) {
          setTotalThoughts(response.total);
        }
      } else {
        setError(response.message || 'Failed to fetch thoughts');
        setThoughts([]);
      }
    } catch (err: any) {
      console.error('Error fetching thoughts:', err);
      setError('Network error occurred');
      setThoughts([]);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for thoughts
  const getMockThoughts = (): ClientThoughtWithAuthor[] => [
    {
      content_id: 1,
      text: 'Climate change is the most pressing issue of our time and requires immediate global action.',
      createdAt: new Date('2023-01-15'),
      updatedAt: new Date('2023-01-15'),
    },
    {
      content_id: 2,
      text: 'Artificial intelligence will fundamentally transform how we work and live in the next decade.',
      createdAt: new Date('2023-02-20'),
      updatedAt: new Date('2023-02-20'),
    },
    {
      content_id: 3,
      text: 'Universal basic income could be a solution to technological unemployment.',
      createdAt: new Date('2023-03-10'),
      updatedAt: new Date('2023-03-10'),
    },
    {
      content_id: 4,
      text: 'The future of education lies in personalized, adaptive learning technologies.',
      createdAt: new Date('2023-04-05'),
      updatedAt: new Date('2023-04-05'),
    },
    {
      content_id: 5,
      text: 'Renewable energy adoption is accelerating faster than most experts predicted.',
      createdAt: new Date('2023-05-12'),
      updatedAt: new Date('2023-05-12'),
    },
  ];

  // Effect to respond to URL parameter changes
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    const urlOrderBy = searchParams.get('orderBy') || 'id';
    const urlOrderDirection = searchParams.get('orderDirection') || 'DESC';
    const urlFilters = {
      createdAfter: searchParams.get('createdAfter') || '',
      createdBefore: searchParams.get('createdBefore') || '',
    };

    // Update local state from URL
    setSearchTerm(urlSearch);
    setOrderBy(urlOrderBy);
    setOrderDirection(urlOrderDirection);
    setFilters(urlFilters);
    setAppliedFilters(urlFilters);

    // Reset page history when URL parameters change (filters, order, search, etc.)
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);

    // Fetch data with URL parameters (always get count on URL changes since this indicates initial load or filter changes)
    fetchThoughts('first', undefined, urlSearch, urlFilters, urlOrderBy, urlOrderDirection, true);
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    updateUrlParams({
      search: searchTerm,
      orderBy,
      orderDirection,
      createdAfter: filters.createdAfter,
      createdBefore: filters.createdBefore,
    });
  };

  const handleSelectThought = (thoughtId: number): void => {
    const newSelected = new Set(selectedThoughts);
    if (newSelected.has(thoughtId)) {
      newSelected.delete(thoughtId);
    } else {
      newSelected.add(thoughtId);
    }
    setSelectedThoughts(newSelected);
  };

  const handleSelectAll = (): void => {
    if (selectedThoughts.size === thoughts.length) {
      setSelectedThoughts(new Set());
    } else {
      setSelectedThoughts(new Set(thoughts.map((thought) => thought.content_id)));
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString();
  };

  // Get cursor values for a specific thought
  const getCursorForThought = (thought: ClientThoughtWithAuthor) => {
    return {
      id: thought.content_id,
      createdDate: new Date(thought.createdAt).toISOString(),
    };
  };

  // Handle next page navigation
  const handleNextPage = (): void => {
    if (thoughts.length === 0) return;

    const lastThought = thoughts[thoughts.length - 1];
    const cursor = getCursorForThought(lastThought);

    let cursorParams: {
      beforeId?: number;
      afterId?: number;
      beforeCreatedDate?: string;
      afterCreatedDate?: string;
    };

    if (orderBy === 'id') {
      cursorParams = orderDirection === 'ASC' ? { afterId: cursor.id } : { beforeId: cursor.id };
    } else {
      cursorParams =
        orderDirection === 'ASC'
          ? { afterCreatedDate: cursor.createdDate }
          : { beforeCreatedDate: cursor.createdDate };
    }

    // Add to page history
    const newPage = currentPage + 1;
    setPageHistory((prev) => [...prev, { cursor: cursorParams, pageNum: newPage }]);
    setCurrentPage(newPage);

    fetchThoughts('next', cursorParams, searchTerm, appliedFilters, orderBy, orderDirection);
  };

  // Handle previous page navigation
  const handlePrevPage = (): void => {
    if (pageHistory.length <= 1) return;

    // Remove current page from history
    const newHistory = pageHistory.slice(0, -1);
    setPageHistory(newHistory);

    // Get previous page info
    const prevPageInfo = newHistory[newHistory.length - 1];
    setCurrentPage(prevPageInfo.pageNum);

    fetchThoughts(
      'prev',
      prevPageInfo.cursor || undefined,
      searchTerm,
      appliedFilters,
      orderBy,
      orderDirection
    );
  };

  // Handle first page navigation
  const handleFirstPage = (): void => {
    setCurrentPage(1);
    setPageHistory([{ cursor: null, pageNum: 1 }]);
    fetchThoughts('first', undefined, searchTerm, appliedFilters, orderBy, orderDirection);
  };

  const handleOrderByChange = (newOrderBy: string): void => {
    updateUrlParams({
      search: searchTerm,
      orderBy: newOrderBy,
      orderDirection,
      createdAfter: appliedFilters.createdAfter,
      createdBefore: appliedFilters.createdBefore,
    });
  };

  const handleOrderDirectionChange = (newDirection: string): void => {
    updateUrlParams({
      search: searchTerm,
      orderBy,
      orderDirection: newDirection,
      createdAfter: appliedFilters.createdAfter,
      createdBefore: appliedFilters.createdBefore,
    });
  };

  const handleFilterChange = (filterName: string, value: string): void => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);
  };

  const applyFilters = (): void => {
    updateUrlParams({
      search: searchTerm,
      orderBy,
      orderDirection,
      createdAfter: filters.createdAfter,
      createdBefore: filters.createdBefore,
    });
    setShowFilters(false);
  };

  const clearFilters = (): void => {
    updateUrlParams({
      search: searchTerm,
      orderBy,
      orderDirection,
      createdAfter: '',
      createdBefore: '',
    });
    setShowFilters(false);
  };

  // Calculate total pages
  const totalPages = totalThoughts > 0 ? Math.ceil(totalThoughts / limit) : 1;

  const getFilterSummary = (): string => {
    const activeParts: string[] = [];

    if (appliedFilters.createdAfter) {
      activeParts.push(`Created After: ${appliedFilters.createdAfter}`);
    }

    if (appliedFilters.createdBefore) {
      activeParts.push(`Created Before: ${appliedFilters.createdBefore}`);
    }

    if (activeParts.length === 0) {
      return `Ordered by ${
        orderBy === 'id' ? 'ID' : 'Created Date'
      } (${orderDirection}). Total: ${totalThoughts}`;
    }

    return `${activeParts.join(', ')} | Ordered by ${
      orderBy === 'id' ? 'ID' : 'Created Date'
    } (${orderDirection}). Total: ${totalThoughts}`;
  };

  const handleRowClick = (thoughtId: number, event: React.MouseEvent): void => {
    // Prevent navigation if clicking on checkbox, button, or action elements
    const target = event.target as HTMLElement;
    if (
      (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') ||
      target.tagName === 'BUTTON' ||
      target.closest('button') ||
      target.closest('.actions')
    ) {
      return;
    }
    // Navigate to thought detail page
    navigate(`/thoughts/${thoughtId}`);
  };

  return (
    <div className="thoughts-page">
      <div className="thoughts-header">
        <div className="header-top">
          <h1>Thoughts</h1>
          <div className="header-stats">
            <span className="stat-item">
              <FileText size={16} />
              {totalThoughts} Total Thoughts
            </span>
          </div>
        </div>

        {/* Search and Controls Row */}
        <div className="search-and-controls">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search thoughts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-secondary">
              Search
            </button>
          </form>

          <div className="controls-row">
            {/* Order By Controls */}
            <div className="order-controls">
              <div className="order-group">
                <label>Order by:</label>
                <select
                  value={orderBy}
                  onChange={(e) => handleOrderByChange(e.target.value)}
                  className="order-select"
                >
                  <option value="id">ID</option>
                  <option value="createdAt">Created Date</option>
                </select>
              </div>
              <div className="order-group">
                <label>Direction:</label>
                <select
                  value={orderDirection}
                  onChange={(e) => handleOrderDirectionChange(e.target.value)}
                  className="order-select"
                >
                  <option value="DESC">
                    <ChevronDown size={16} /> Descending
                  </option>
                  <option value="ASC">
                    <ChevronUp size={16} /> Ascending
                  </option>
                </select>
              </div>
            </div>

            <button className="btn-secondary" onClick={() => setShowFilters(!showFilters)}>
              <Filter size={16} />
              Filter{' '}
              {Object.values(appliedFilters).some((f) => f) && (
                <span className="filter-badge">•</span>
              )}
            </button>
          </div>
        </div>

        <div className="filter-summary">{getFilterSummary()}</div>

        {showFilters && (
          <div className="filters-dropdown">
            <div className="filters-header">
              <h3>Filters</h3>
              <button className="btn-text" onClick={clearFilters}>
                Clear All
              </button>
            </div>

            <div className="filter-group">
              <label>Created After:</label>
              <input
                type="date"
                placeholder="Enter Created After"
                value={filters.createdAfter}
                onChange={(e) => handleFilterChange('createdAfter', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Created Before:</label>
              <input
                type="date"
                placeholder="Enter Created Before"
                value={filters.createdBefore}
                onChange={(e) => handleFilterChange('createdBefore', e.target.value)}
              />
            </div>

            <div className="filters-actions">
              <button className="btn-secondary" onClick={() => setShowFilters(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={applyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        )}

        {selectedThoughts.size > 0 && (
          <div className="bulk-actions">
            <span>{selectedThoughts.size} selected</span>
            <button className="btn-secondary">
              <Edit2 size={14} />
              Edit
            </button>
            <button className="btn-danger">
              <Trash2 size={14} />
              Delete
            </button>
          </div>
        )}
      </div>

      <div className="thoughts-content">
        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
            <p>Showing mock data instead.</p>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading thoughts...</div>
        ) : (
          <>
            <div className="thoughts-table-container">
              <table className="thoughts-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={selectedThoughts.size === thoughts.length && thoughts.length > 0}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>Content ID</th>
                    <th>Thought Text</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {thoughts.map((thought) => (
                    <tr
                      key={thought.content_id}
                      onClick={(e) => handleRowClick(thought.content_id, e)}
                      className="thought-row"
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedThoughts.has(thought.content_id)}
                          onChange={() => handleSelectThought(thought.content_id)}
                        />
                      </td>
                      <td>
                        <span className="content-id">{thought.content_id}</span>
                      </td>
                      <td>
                        <div className="thought-text">{thought.text}</div>
                      </td>
                      <td>{formatDate(thought.createdAt)}</td>
                      <td>{formatDate(thought.updatedAt)}</td>
                      <td>
                        <div className="actions">
                          <button className="action-btn" title="View Details">
                            <Eye size={14} />
                          </button>
                          <button className="action-btn" title="Edit">
                            <Edit2 size={14} />
                          </button>
                          <button className="action-btn" title="More Options">
                            <MoreVertical size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <div className="pagination-info">Showing {thoughts.length} thoughts</div>
              <div className="pagination-controls">
                <button
                  onClick={handleFirstPage}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  First
                </button>
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  Previous
                </button>
                <span className="page-info">Page {currentPage}</span>
                <button onClick={handleNextPage} className="btn-secondary">
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Thoughts;
